import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { Router } from '@angular/router';
import { IData } from 'app/core/models/api/data';
import { IDentistryValidationUnions } from 'app/core/models/dentistry/dentistry';
import {
    IConfigurationScreen,
    IGetConfigurationScreen,
    IGetMainSelectionScreen,
    ISelectionScreen,
    ITranslation,
} from 'app/core/models/dentistry/translation';
import { IError } from 'app/core/models/error/error';
import { environment } from 'environments/environment';
import { catchError, map, Observable, throwError } from 'rxjs';
import { IGetAllDentistrySentMaterial, ISentMaterial } from '../../models/dentistry/sent-material';
import { IDentistryV2ValidationUnions } from 'app/core/models/dentistry/dentistry-v2';

export const urlGetMainSelectionScreen = () => ['clinic', 'admin', 'dentistry', 'selection', 'main'];
export const urlGetSelectionScreen = (id: number) => ['clinic', 'admin', 'dentistry', 'selection', id];
export const urlDentistryValidateUnions = () => ['clinic', 'admin', 'dentistry', 'validate-unions'];
export const urlGetDentistryTranslation = () => ['clinic', 'admin', 'dentistry', 'translation'];
const urlGetAllDentistrySentMaterial = () => ['clinic', 'admin', 'dentistry', 'sent-material'];

@Service()
export class DentistryService {
    private readonly router = inject(Router);
    private readonly httpClient = inject(HttpClient);

    public getMainSelectionScreen(data: IGetMainSelectionScreen): Observable<IData<ISelectionScreen>> {
        const url = this.router.createUrlTree(urlGetMainSelectionScreen(), {
            queryParams: data,
        });

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.get<IData<ISelectionScreen>>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public getSelectionScreen(data: IGetConfigurationScreen): Observable<IData<ISelectionScreen>> {
        const { id, ...queryParams } = data;

        const url = this.router.createUrlTree(urlGetSelectionScreen(id), {
            queryParams,
        });

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.get<IData<ISelectionScreen>>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public getConfigurationScreen(data: IGetConfigurationScreen): Observable<IData<IConfigurationScreen>> {
        const { id, ...parameters } = data;
        const baseUrl = ['clinic', 'admin', 'dentistry', 'configuration', id];

        const url = this.router.createUrlTree(baseUrl, {
            queryParams: parameters,
        });

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.get<IData<IConfigurationScreen>>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public dentistryValidateUnions(data: IDentistryValidationUnions): Observable<IData<IConfigurationScreen>> {
        const url = this.router.createUrlTree(urlDentistryValidateUnions());

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.post<IData<IConfigurationScreen>>(parsedUrl, data, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public getDentistryTranslation(): Observable<IData<ITranslation[]>> {
        const url = this.router.createUrlTree(urlGetDentistryTranslation());

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.get(parsedUrl, { responseType: 'json' }).pipe(
            map((res: IData<ITranslation[]>) => {
                return res;
            }),
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public getAllDentistrySentMaterial(data: IGetAllDentistrySentMaterial): Observable<IData<ISentMaterial[]>> {
        const url = this.router.createUrlTree(urlGetAllDentistrySentMaterial(), {
            queryParams: data,
        });

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.get<IData<ISentMaterial[]>>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    // V2
    dentistryV2ValidateUnions(data: IDentistryV2ValidationUnions): Observable<null> {
        const url = this.router.createUrlTree(urlDentistryValidateUnions());

        const parsedUrl = `${environment.apiUrl}/v2${url}`;

        return this.httpClient.post<null>(parsedUrl, data, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }
}
